import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	inject,
	signal,
} from '@angular/core';
import { Router } from '@angular/router';
import type {
	OperatorReservation,
	ReservationStatus,
} from '@pressteaze/domain';
import { OperatorReservationCardComponent } from '../reservation/reservation-card/reservation-card.component';
import {
	OperatorReservationEditorComponent,
	type ReservationEditorMode,
} from '../reservation/reservation-editors/reservation-editor.component';
import { OperatorStateService } from '../../services/state-services/operator-state.service';

@Component({
	selector: 'app-operator-dashboard',
	imports: [
		OperatorReservationCardComponent,
		OperatorReservationEditorComponent,
	],
	templateUrl: './operator-dashboard.component.html',
	styleUrl: './operator-dashboard.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperatorDashboardComponent implements OnInit {
	readonly operatorStateSrv = inject(OperatorStateService);
	private readonly router = inject(Router);
	readonly expandedReservationId = signal<string | null>(null);
	readonly editorMode = signal<ReservationEditorMode | null>(null);
	readonly editing = signal<OperatorReservation | null>(null);

	ngOnInit(): void {
		void this.operatorStateSrv.activateDashboard();
	}

	async openShift(): Promise<void> {
		await this.router.navigateByUrl('/shift');
	}

	openEdit(reservation: OperatorReservation): void {
		this.editing.set(reservation);
		this.editorMode.set('edit');
	}

	closeEditor(): void {
		this.editing.set(null);
		this.editorMode.set(null);
	}

	async transition(
		reservation: OperatorReservation,
		status: ReservationStatus,
	): Promise<void> {
		await this.operatorStateSrv.transition(reservation, status);
	}

	toggleReservation(reservationId: string): void {
		this.expandedReservationId.update((current) =>
			current === reservationId ? null : reservationId,
		);
	}

	formatLongDate(value: string): string {
		const formatted = new Intl.DateTimeFormat('el-GR', {
			weekday: 'long',
			day: 'numeric',
			month: 'long',
		}).format(this.localDate(value));
		return formatted.charAt(0).toUpperCase() + formatted.slice(1);
	}

	formatDateRange(fromDate: string, toDate: string): string {
		const fromParts = this.dateParts(this.localDate(fromDate));
		const toParts = this.dateParts(this.localDate(toDate));
		if (
			fromParts['month'] === toParts['month'] &&
			fromParts['year'] === toParts['year']
		) {
			return fromParts['day'] + '–' + toParts['day'] + ' ' + toParts['month'];
		}
		return (
			fromParts['day'] +
			' ' +
			fromParts['month'] +
			'–' +
			toParts['day'] +
			' ' +
			toParts['month']
		);
	}

	formatTime(value: string | null): string {
		if (!value) return '—';

		return new Intl.DateTimeFormat('el-GR', {
			timeZone: this.operatorStateSrv.context()?.timezone,
			hour: '2-digit',
			minute: '2-digit',
			hourCycle: 'h23',
		}).format(new Date(value));
	}

	averageGuests(guestCount: number, reservationCount: number): string {
		if (!reservationCount) return '0';

		return new Intl.NumberFormat('el-GR', {
			maximumFractionDigits: 1,
		}).format(guestCount / reservationCount);
	}

	noShowRate(noShowCount: number, reservationCount: number): string {
		if (!reservationCount) return '0';

		return new Intl.NumberFormat('el-GR', {
			maximumFractionDigits: 1,
		}).format((noShowCount / reservationCount) * 100);
	}

	private localDate(value: string): Date {
		return new Date(value + 'T12:00:00Z');
	}

	private dateParts(date: Date): Record<string, string> {
		return Object.fromEntries(
			new Intl.DateTimeFormat('el-GR', {
				day: 'numeric',
				month: 'short',
				year: 'numeric',
			})
				.formatToParts(date)
				.filter((part) => ['day', 'month', 'year'].includes(part.type))
				.map((part) => [part.type, part.value.replace('.', '')]),
		);
	}
}
